import React, { FC, ComponentProps } from 'react'

import styled, { css } from 'styled-components'
import { Card } from '../card'
import { ThemeColors } from '../theme/colors'
import { InputState } from './InputState'
import { Props as FieldProps } from './Field'

type Props = {
  children: React.ReactNode
  hoverColor?: ThemeColors
  inputState: InputState
  isFillBgOnHover?: boolean
  onWhite?: boolean
} & FieldProps

const Placeholder = styled(Card)<ComponentProps<typeof Card> & Props>`
  transition: 0.1s;
  ${({ isFillBgOnHover, hoverColor, theme, inputState }) =>
    isFillBgOnHover && inputState === InputState.Idle
      ? css`
          &:hover {
            background: ${theme.colors[hoverColor ?? 'neutral-dark-4']};
          }
        `
      : ''}
  box-shadow: ${({ onWhite, boxShadow }) =>
    boxShadow || (onWhite && 'inset 0px 0px 0px 1px rgba(142, 168, 190, 0.16)')};
  border-bottom-left-radius: ${({ focused, borderBottomLeftRadius = 0 }) =>
    focused && borderBottomLeftRadius};
  border-bottom-right-radius: ${({ focused, borderBottomRightRadius = 0 }) =>
    focused && borderBottomRightRadius};
  ${({ inputState, theme, focused }) => {
    switch (inputState) {
      case InputState.Disabled: {
        return css`
          box-shadow: none;
          background: ${theme.colors['neutral-dark-4']};
          color: ${theme.colors['neutral-dark-24']};
        `
      }

      case InputState.Focused: {
        return css`
          background: ${theme.colors['teal-dark-8']};
          box-shadow: inset 0px 0px 0px 1px rgba(41, 52, 61, 0.04), inset 0px -2px 0px #00a1c9;
        `
      }

      case InputState.Invalid: {
        return css`
          box-shadow: ${focused ? 'inset 0px -2px 0px #E96563' : 'none'};
          background: ${theme.colors['red-dark-8']};
        `
      }
    }
  }}
`

Placeholder.displayName = 'Placeholder'

export const Container: FC<Props> = ({
  inputState,
  children,
  isFillBgOnHover,
  small,
  h,
  hoverColor,
  ...others
}: Props) => {
  return (
    <Placeholder
      p={0}
      inputState={inputState}
      minHeight={small ? 7 : 9}
      height={small ? 7 : h}
      display='flex'
      flexDirection='column'
      justifyContent='center'
      isFillBgOnHover={isFillBgOnHover}
      hoverColor={hoverColor}
      {...others}
    >
      {children}
    </Placeholder>
  )
}
